import React from 'react'

import useParseLinkUrlWithLocale from '@/lib/hooks/routing/useParseLinkUrlWithLocale'
import type { HeaderColumn } from '@/components/_headers/headerData'
import type {
  CosmicProductType,
  CosmicVariationType,
} from '@/lib/cms/cosmic/types/CosmicProductType'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/_ui/Accordion'
import useGlobalStore from '@/store/useGlobalStore'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { trackShoSelectProduct } from '@/lib/services/mixpanel/events/productEvents'

type Props = {
  columns: [HeaderColumn, HeaderColumn?, HeaderColumn?]
}

export const MobileMenuColumns = ({ columns }: Props) => {
  const { parseLinkUrlWithLocale } = useParseLinkUrlWithLocale()
  const { setApp } = useGlobalStore()
  const { gtm } = useGTM()
  const {
    user: { customerType },
    app: { marketExceptions },
  } = useGlobalStore()

  return (
    <Accordion type="single" collapsible>
      {columns.map((column, index) => {
        if (!(column?.title && column.title.length > 0)) {
          return null
        }

        return (
          <AccordionItem
            key={column?.title}
            value={column?.title || `${index}`}
          >
            <AccordionTrigger>{column?.title}</AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-col space-y-3 border-l pl-5">
                {column?.items?.map((item) => {
                  const isProductLink = item.href.includes('/product/')

                  return (
                    <a
                      onClick={async (event) => {
                        event.preventDefault()

                        gtm.navbarMenuItemClicked({
                          action: item.title,
                          category: column.title,
                        })

                        await mixpanelService
                          .trackEvent(
                            MixpanelEvent.NAVIGATION_BAR_SHOP_MENU_ITEM_CLICKED,
                            {
                              action:
                                (item.href.at(-1) === '/'
                                  ? item.href.split('/').at(-2)
                                  : item.href.split('/').at(-1)) || '',
                              category: column.title,
                            },
                          )
                          .catch(() => {})

                        if (isProductLink) {
                          const productForTracking: CosmicProductType = {
                            name: item.title,
                            slug:
                              item.href.split('/').pop() ||
                              item.title.toLowerCase().replaceAll(/\s+/g, '-'),
                            description: '',
                            variations: [
                              {
                                sku: '',
                                combinationName: item.title,
                                pricing: null,
                                isDefault: true,
                                isDisable: false,
                                partsLabel: '',
                              } as CosmicVariationType,
                            ],
                            attributes: [],
                            availableFor: {
                              customer: true,
                              member: true,
                              associate: true,
                              employee: true,
                            },
                          }

                          await trackShoSelectProduct({
                            product: productForTracking,
                            selectedVariation: productForTracking.variations[0],
                            eventLocation: 'navigation_bar',
                            action: 'navigation_bar_shop_menu_link_clicked',
                            customerType,
                            currencyCode:
                              marketExceptions?.currencyCode || 'USD',
                          }).catch((error) => {
                            console.error(
                              'Product link tracking failed (mobile):',
                              error,
                            )
                          })
                        }

                        setApp({ isMobileDrawerOpen: false })

                        window.location.href = parseLinkUrlWithLocale({
                          url: item.href,
                        })
                      }}
                      className="text-primary no-underline hover:underline"
                      key={item.title}
                      href={parseLinkUrlWithLocale({ url: item.href })}
                    >
                      {item.title}
                    </a>
                  )
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )
      })}
    </Accordion>
  )
}
